import { DownloadButton } from '@/features/spark-storage/DownloadButton';
import { FileDetailView } from '@/features/spark-storage/FileDetailView';
import { StorageTable } from '@/features/spark-storage/StorageTable';
import { UploadButton } from '@/features/spark-storage/UploadButton';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { storageDocumentsQueryOptions, type Document } from '../api/queries/storage';
import { useCanvasContext } from '../hooks/useCanvasContext';

export default function Storage() {
  const { t: tCommon } = useTranslation();
  const { t: tStorage } = useTranslation('storage');

  const { isCanvas } = useCanvasContext();
  const { objectName, recordId } = useParams<{ objectName?: string; recordId?: string }>();
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<Document | null>(null);

  const { data, isLoading, error } = useQuery(
    storageDocumentsQueryOptions(objectName || '', recordId || '')
  );

  useEffect(() => {
    if (error) {
      toast.error(tStorage('errorLoadingDocuments', { message: error.message }), {
        richColors: true,
      });
    }
  }, [error, tStorage]);

  const handleDocumentSelect = (documentId: string, selected: boolean) => {
    setSelectedDocuments(prev =>
      selected ? [...prev, documentId] : prev.filter(id => id !== documentId)
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedDocuments(selected ? data?.documents.map(doc => doc.id) || [] : []);
  };

  const handleDownloadComplete = () => {
    setSelectedDocuments([]);
  };

  const handleFileClick = (document: Document) => {
    setSelectedFile(document);
  };

  const handleBackToFiles = () => {
    setSelectedFile(null);
  };

  if (!recordId) {
    return (
      <div className={`${isCanvas ? 'p-4 h-full' : 'p-6'}`}>
        <h1 className={`${isCanvas ? 'text-xl' : 'text-2xl'} font-bold mb-4`}>
          {isCanvas ? tStorage('storageCanvas') : tStorage('storagePage')}
        </h1>
        <p>{tStorage('noRecordIdMessage')}</p>
        <p className="text-sm text-gray-600 mt-2">{tStorage('noRecordIdNavigation')}</p>
      </div>
    );
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) {
      return tCommon('format.fileSize', {
        size: 0,
        formatParams: {
          size: {
            style: 'unit',
            unit: 'byte',
            unitDisplay: 'long',
            minimumFractionDigits: 0,
          },
        },
      });
    }

    const k = 1024;
    const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return tCommon('format.fileSize', {
      size,
      formatParams: {
        size: {
          style: 'unit',
          unit: units[i],
          unitDisplay: 'long',
          maximumFractionDigits: 1,
        },
      },
    });
  };

  if (selectedFile) {
    return (
      <div className={`${isCanvas ? 'bg-white' : 'p-4'} w-full h-full `}>
        <FileDetailView
          document={selectedFile}
          onBack={handleBackToFiles}
          formatFileSize={formatFileSize}
        />
      </div>
    );
  }

  return (
    <div className={`${isCanvas ? 'h-full' : ''} bg-white w-full flex flex-col`}>
      <div className="flex items-center justify-between p-6 border border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          {isCanvas && (
            <h1
              className={`text-2xl font-semibold ${isCanvas ? 'text-gray-900' : ''} font-montserrat`}
            >
              {tCommon('app.name')}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-3">
          <DownloadButton
            selectedDocuments={selectedDocuments}
            documents={data?.documents || []}
            onDownloadComplete={handleDownloadComplete}
          />

          <UploadButton
            recordId={recordId}
            objectName={objectName}
            formatFileSize={formatFileSize}
            onUploadSuccess={() => {
              // Refetch the documents after successful upload
              // The query will automatically refetch due to invalidation
            }}
          />
        </div>
      </div>

      <div className="overflow-y-scroll border border-gray-200 rounded-b-lg pb-1">
        <StorageTable
          documents={data?.documents || []}
          isLoading={isLoading}
          formatFileSize={formatFileSize}
          selectedDocuments={selectedDocuments}
          onDocumentSelect={handleDocumentSelect}
          onSelectAll={handleSelectAll}
          onFileClick={handleFileClick}
        />
      </div>
    </div>
  );
}
